import { useState, useCallback, useEffect } from "react";
import {
  supabase,
  type BuildingData,
  type InsertBuildingData,
  type BuildingStructureBox,
  type TenantData,
} from "@/utils/supabase";
import { getBuildingStructure } from "../config/buildingPresets";
import { GRID_CONFIG } from "../config/grid";
import { useAuthStore } from "@/stores/authStore";
import type { TenantInstance } from "../config/types";

/**
 * 랜덤한 밝은 컬러 생성 (pastel 톤)
 */
function generateRandomBrightColor(): string {
  const hue = Math.floor(Math.random() * 360); // 0-360
  const saturation = 60 + Math.floor(Math.random() * 20); // 60-80%
  const lightness = 55 + Math.floor(Math.random() * 15); // 55-70%

  // HSL을 HEX로 변환
  const hslToHex = (h: number, s: number, l: number): string => {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };

  return hslToHex(hue, saturation, lightness);
}

/**
 * DB 좌표(정수형, 셀 좌하단 기준)를 렌더링 좌표(셀 중앙)로 변환
 * 예: CELL_SIZE=5일 때, 200 -> 202.5 (셀 중앙)
 */
function toRenderPosition(value: number): number {
  const halfCell = GRID_CONFIG.CELL_SIZE / 2;
  return value + halfCell;
}

export interface Building {
  id: string;
  /** 렌더링용 위치 (셀 중앙) */
  position: [number, number, number];
  /** DB 저장 위치 (정수형, 셀 좌하단) */
  dbPosition: [number, number, number];
  meshIndex: number;
  buildingStructure?: BuildingStructureBox[] | null;
  buildingText?: string | null;
  title?: string | null;
  message1?: string | null;
  message2?: string | null;
  userId: string;
  /** 사용자 이름 (user_profiles 조인 결과) */
  username?: string | null;
  /** 사용자 컬러 (user_profiles 조인 결과) */
  userColor?: string | null;
  /** 입주자 목록 (tenants 조인 결과) */
  tenants: TenantInstance[];
}

/**
 * 건물 영속성 훅
 * Supabase와 실시간 동기화
 */
export function useBuildingPersistence() {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 빌딩 로드 함수
  const loadBuildings = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("buildings")
        .select(
          `
            *,
            user_profiles(user_color, username),
            tenants(*)
          `
        )
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error loading buildings:", error);
        return;
      }

      if (data) {
        const loadedBuildings: Building[] = data.map(
          (building: BuildingData & { tenants?: TenantData[] }) => {
            // tenants 데이터 변환
            const tenantInstances: TenantInstance[] = (
              building.tenants || []
            ).map((t: TenantData) => ({
              id: t.id,
              buildingId: t.building_id,
              userId: t.user_id,
              text: t.text,
              projectionAxis: t.projection_axis,
              positionU: t.position_u,
              positionV: t.position_v,
            }));

            return {
              id: building.id,
              position: [
                toRenderPosition(building.position_x),
                building.position_y,
                toRenderPosition(building.position_z),
              ],
              dbPosition: [
                building.position_x,
                building.position_y,
                building.position_z,
              ],
              meshIndex: building.mesh_index,
              buildingStructure:
                building.building_structure ||
                getBuildingStructure(building.mesh_index),
              buildingText: building.building_text,
              title: building.title,
              message1: building.message1,
              message2: building.message2,
              userId: building.user_id,
              username: building.user_profiles?.username || null,
              userColor: building.user_profiles?.user_color || null,
              tenants: tenantInstances,
            };
          }
        );
        setBuildings(loadedBuildings);
      }
    } catch (error) {
      console.error("Error loading buildings:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 초기 로드
  useEffect(() => {
    loadBuildings();
  }, [loadBuildings]);

  // Realtime 구독
  useEffect(() => {
    console.log("[BuildingPersistence] Setting up Realtime subscription...");

    const channel = supabase
      .channel("buildings-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "buildings" },
        (payload) => {
          console.log(
            "[BuildingPersistence] Realtime event:",
            payload.eventType
          );

          if (payload.eventType === "INSERT") {
            const newBuilding = payload.new as BuildingData;
            // Realtime 이벤트는 조인 데이터가 없으므로, 해당 건물의 user_profiles를 별도로 조회
            const fetchUserColor = async () => {
              try {
                const { data: profileData } = await supabase
                  .from("user_profiles")
                  .select("user_color")
                  .eq("id", newBuilding.user_id)
                  .single();

                setBuildings((prev) => {
                  if (prev.some((b) => b.id === newBuilding.id)) {
                    // 이미 존재하면 userColor만 업데이트
                    return prev.map((b) =>
                      b.id === newBuilding.id
                        ? { ...b, userColor: profileData?.user_color || null }
                        : b
                    );
                  }
                  // 새로 추가
                  return [
                    ...prev,
                    {
                      id: newBuilding.id,
                      position: [
                        toRenderPosition(newBuilding.position_x),
                        newBuilding.position_y,
                        toRenderPosition(newBuilding.position_z),
                      ],
                      dbPosition: [
                        newBuilding.position_x,
                        newBuilding.position_y,
                        newBuilding.position_z,
                      ],
                      meshIndex: newBuilding.mesh_index,
                      buildingStructure:
                        newBuilding.building_structure ||
                        getBuildingStructure(newBuilding.mesh_index),
                      buildingText: newBuilding.building_text,
                      title: newBuilding.title,
                      message1: newBuilding.message1,
                      message2: newBuilding.message2,
                      userId: newBuilding.user_id,
                      userColor: profileData?.user_color || null,
                      tenants: [], // 새 건물은 입주자 없음
                    },
                  ];
                });
              } catch (error) {
                console.error(
                  "Error fetching user color for new building:",
                  error
                );
                // 에러 발생 시 userColor 없이 추가
                setBuildings((prev) => {
                  if (prev.some((b) => b.id === newBuilding.id)) return prev;
                  return [
                    ...prev,
                    {
                      id: newBuilding.id,
                      position: [
                        toRenderPosition(newBuilding.position_x),
                        newBuilding.position_y,
                        toRenderPosition(newBuilding.position_z),
                      ],
                      dbPosition: [
                        newBuilding.position_x,
                        newBuilding.position_y,
                        newBuilding.position_z,
                      ],
                      meshIndex: newBuilding.mesh_index,
                      buildingStructure:
                        newBuilding.building_structure ||
                        getBuildingStructure(newBuilding.mesh_index),
                      buildingText: newBuilding.building_text,
                      title: newBuilding.title,
                      message1: newBuilding.message1,
                      message2: newBuilding.message2,
                      userId: newBuilding.user_id,
                      userColor: null,
                      tenants: [], // 새 건물은 입주자 없음
                    },
                  ];
                });
              }
            };

            fetchUserColor();
          } else if (payload.eventType === "UPDATE") {
            const updated = payload.new as BuildingData;
            setBuildings((prev) =>
              prev.map((b) =>
                b.id === updated.id
                  ? {
                      ...b,
                      buildingText: updated.building_text,
                      title: updated.title,
                      message1: updated.message1,
                      message2: updated.message2,
                    }
                  : b
              )
            );
          } else if (payload.eventType === "DELETE") {
            const deleted = payload.old as BuildingData;
            setBuildings((prev) => prev.filter((b) => b.id !== deleted.id));
          }
        }
      )
      .subscribe((status) => {
        console.log("[BuildingPersistence] Subscription status:", status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // 건물 생성
  const createBuilding = useCallback(
    async (
      data: Omit<InsertBuildingData, "user_id"> & {
        building_structure?: BuildingStructureBox[];
      },
      meshIndex: number
    ): Promise<BuildingData | null> => {
      const user = useAuthStore.getState().user;
      if (!user) {
        console.error("User not authenticated");
        return null;
      }

      try {
        // user_profiles에서 로그인 시 이미 생성되므로 별도 체크 불필요
        // user.id가 이미 user_profiles.id임
        // 커스텀 building_structure가 전달되면 사용, 아니면 프리셋에서 가져옴
        const buildingStructure =
          data.building_structure || getBuildingStructure(meshIndex);

        const insertData: InsertBuildingData = {
          position_x: data.position_x,
          position_y: data.position_y,
          position_z: data.position_z,
          mesh_index: data.mesh_index,
          building_text: data.building_text,
          title: data.title,
          building_structure: buildingStructure,
          user_id: user.id,
        };

        const { data: result, error } = await supabase
          .from("buildings")
          .insert(insertData)
          .select()
          .single();

        if (error) {
          console.error("Error creating building:", error);
          return null;
        }

        return result;
      } catch (error) {
        console.error("Error creating building:", error);
        return null;
      }
    },
    []
  );

  // 건물 삭제
  const deleteBuilding = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from("buildings").delete().eq("id", id);

      if (error) {
        console.error("Error deleting building:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error deleting building:", error);
      return false;
    }
  }, []);

  // 건물 업데이트
  const updateBuilding = useCallback(
    async (
      id: string,
      updates: Partial<InsertBuildingData>
    ): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from("buildings")
          .update(updates)
          .eq("id", id);

        if (error) {
          console.error("Error updating building:", error);
          return false;
        }

        return true;
      } catch (error) {
        console.error("Error updating building:", error);
        return false;
      }
    },
    []
  );

  return {
    buildings,
    isLoading,
    createBuilding,
    deleteBuilding,
    updateBuilding,
    loadBuildings, // 수동 새로고침용
  };
}
